import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { ProjectCarouselCanvasProps } from '../projects.types';
import './ProjectCarouselCanvas.styles.css';

// * 1x1 transparent decoy PNG data URL
const DECOY_BLANK_DATA_URI =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxIiBoZWlnaHQ9IjEiPjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IiMwMDAwMDAiLz48L3N2Zz4=';

/**
 * Renders in-memory project imagery directly onto a high-DPI canvas.
 * Enforces the "Decoy Overlay Shield" to prevent right-click asset harvesting.
 */
export const ProjectCarouselCanvas: React.FC<ProjectCarouselCanvasProps> = ({
  images,
  title,
}) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const activeBitmapRef = useRef<ImageBitmap | HTMLImageElement | null>(null);

  const totalImages = Math.max(1, images.length);
  const currentAssetPath = images[currentIndex] || '';

  // * Repaints the currently active bitmap onto the high-DPI canvas context
  const redrawCanvas = useCallback(() => {
    try {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      const bitmap = activeBitmapRef.current;
      if (!canvas || !container || !bitmap) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      // * Adjust buffer dimensions to physical screen pixels for zero blur
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // ? Compute letterbox / aspect-fit rendering bounds
      const bitmapWidth = 'width' in bitmap ? bitmap.width : 800;
      const bitmapHeight = 'height' in bitmap ? bitmap.height : 500;
      const hRatio = canvas.width / bitmapWidth;
      const vRatio = canvas.height / bitmapHeight;
      const ratio = Math.min(hRatio, vRatio);

      const drawW = bitmapWidth * ratio;
      const drawH = bitmapHeight * ratio;
      const drawX = (canvas.width - drawW) / 2;
      const drawY = (canvas.height - drawH) / 2;

      ctx.drawImage(bitmap, 0, 0, bitmapWidth, bitmapHeight, drawX, drawY, drawW, drawH);
    } catch (err) {
      console.error('[ProjectCarouselCanvas] Canvas redraw error:', err);
    }
  }, []);

  // * Fetches and decodes the image asset entirely in memory
  useEffect(() => {
    let isCancelled = false;
    setIsLoading(true);

    const loadImageInMemory = async (): Promise<void> => {
      try {
        if (!currentAssetPath) {
          setIsLoading(false);
          return;
        }

        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = currentAssetPath;

        await img.decode();
        if (isCancelled) return;

        activeBitmapRef.current = img;
        setIsLoading(false);
        redrawCanvas();
      } catch (err) {
        // ! Fallback gracefully if image decode fails
        console.warn(`[ProjectCarouselCanvas] Failed to decode ${currentAssetPath}:`, err);
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadImageInMemory();

    return () => {
      isCancelled = true;
    };
  }, [currentAssetPath, redrawCanvas]);

  // * Keep canvas buffer dimensions aligned with responsive viewport updates
  useEffect(() => {
    const handleResize = () => {
      redrawCanvas();
    };
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, [redrawCanvas]);

  // * Navigation handlers
  const handlePrev = (e: React.MouseEvent): void => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : totalImages - 1));
  };

  const handleNext = (e: React.MouseEvent): void => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev < totalImages - 1 ? prev + 1 : 0));
  };

  return (
    <div ref={containerRef} className="carousel-stage">
      {/* * Layer 1: High-DPI Canvas Rendering Context */}
      <canvas ref={canvasRef} className="carousel-canvas" aria-label={`${title} screenshot`} />

      {/* * Layer 2: Sub-pixel Carbon Grid Pattern Watermark */}
      <div className="watermark-overlay" aria-hidden="true" />

      {/* * Layer 3: Invisible Decoy Shield (Captures Right-Click and Drag Events) */}
      <div
        className="decoy-shield-layer"
        title={title}
        onContextMenu={(e) => {
          // ? Captures the contextmenu event on the decoy shield
          e.stopPropagation();
        }}
      >
        <img
          src={DECOY_BLANK_DATA_URI}
          alt="Protected Asset"
          className="decoy-shield-asset"
          draggable="true"
        />
      </div>

      {/* * Layer 4: Pulsating Skeleton Loader */}
      {isLoading && <div className="carousel-skeleton" />}

      {/* * Layer 5: Rounded Navigation Controls */}
      {totalImages > 1 && (
        <>
          <button
            type="button"
            className="carousel-nav-btn prev"
            onClick={handlePrev}
            aria-label="Previous Project Asset"
          >
            <i className="fas fa-chevron-left" />
          </button>
          <button
            type="button"
            className="carousel-nav-btn next"
            onClick={handleNext}
            aria-label="Next Project Asset"
          >
            <i className="fas fa-chevron-right" />
          </button>
        </>
      )}

      {/* * Layer 6: Meta bar with 3 dots & index counter badge */}
      <div className="carousel-meta-bar">
        <div className="carousel-dots">
          {images.slice(0, 3).map((_, idx) => (
            <span
              key={idx}
              className={`carousel-dot ${idx === currentIndex % 3 ? 'active' : ''}`}
            />
          ))}
        </div>
        <div className="carousel-counter-badge">
          {currentIndex + 1}/{totalImages}
        </div>
      </div>
    </div>
  );
};

export default ProjectCarouselCanvas;
